// ============================================================================
// ClassGrid (F-752) — echelle de la classe (lecture) + affectation (ClassAssignForm)
// ============================================================================
// Vue GESTION : l'echelle de la classe courante (echelon actuel surligne, au-dela
// du plafond du niveau estompe) + le formulaire d'affectation. M-755 : position
// hors grille DERIVEE du taux reel (offGridPosition). Le tooltip role_fit_note ne
// s'affiche QUE cote gestion (matrice §G).

import { useTranslation } from '../../i18n';
import { Card, CardHeader, Badge } from './ui';
import { ClassAssignForm } from './ClassAssignForm';
import { gridClassToLib } from './compute';
import { resolveNiveau, maxEchelonForNiveau } from '../../lib/salary/niveau';
import { offGridPosition, offGridBadgeLabel } from '../../lib/salary/off-grid';
import { round2 } from '../../lib/salary/projection';
import type { GridClass, ManagementCompensationRecord } from '../../hooks/useManagement';
import { formatHourly, formatSignedHourly, formatSignedPct } from '../salary/format';

export function ClassGrid({
  email,
  record,
  classes,
  action,
  onError,
}: {
  email: string;
  record: ManagementCompensationRecord | null;
  classes: GridClass[];
  /** F-757 SalaryHistoryDialog trigger, injecte par le hub (evite un cycle d'import). */
  action?: React.ReactNode;
  onError: (code: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const currentClass = record?.classId ? classes.find((c) => c.id === record.classId) ?? null : null;
  const libClass = currentClass ? gridClassToLib(currentClass) : null;
  const currentNiveau = libClass ? resolveNiveau(libClass.niveaux, record?.currentNiveau ?? null) : null;
  const cap = libClass ? maxEchelonForNiveau(libClass, record?.currentNiveau ?? null) : null;

  const offGrid = record?.offGrid ?? false;
  const roleFit = record?.roleFitFlag ?? false;
  const ogPos = offGrid && libClass ? offGridPosition(record?.currentHourlyRate ?? null, libClass, record?.currentNiveau ?? null) : null;

  return (
    <Card>
      <CardHeader
        title={t('gest:management.class.title')}
        action={action}
      />
      <div className="mb-3 flex flex-wrap items-center gap-2">
        {currentClass ? (
          <Badge tone="info">
            {currentClass.label}
            {' · '}
            {currentNiveau ? t('gest:management.class.niveauShort', { level: currentNiveau.level }) : t('gest:management.class.niveauUndefined')}
            {offGrid ? ` · ${t('gest:management.class.offGridShort')}` : ` · ${t('gest:management.class.echelonOf', { echelon: record?.currentEchelon ?? 0, total: currentClass.echelons.length })}`}
            {roleFit ? ' *' : ''}
          </Badge>
        ) : (
          <Badge tone="neutral">{t('gest:management.class.notConfigured')}</Badge>
        )}
        {/* §G : la note role_fit ne s'affiche QUE cote gestion (ici). */}
        {roleFit && record?.roleFitNote ? (
          <span className="text-chip text-slate-500 dark:text-slate-400" title={record.roleFitNote}>
            {t('gest:management.class.roleFitBadge')}
          </span>
        ) : null}
        {offGrid ? (
          <Badge tone="warning">
            {offGridBadgeLabel(ogPos)}
            {ogPos
              ? ` · ${formatSignedHourly(ogPos.deltaAbs, locale)}/h${ogPos.deltaPct != null ? ` (${formatSignedPct(ogPos.deltaPct, locale)})` : ''}`
              : ` · ${t('gest:management.class.realRateRequired')}`}
          </Badge>
        ) : null}
      </div>

      {/* §G : la raison PRIVEE hors grille s'affiche ICI (vue gestion) uniquement. */}
      {offGrid && record?.offGridReasonPrivate ? (
        <p className="mb-3 text-meta text-slate-500 dark:text-slate-400">
          {t('gest:management.class.reasonPrivateShown')} <span className="text-slate-700 dark:text-slate-200">{record.offGridReasonPrivate}</span>
        </p>
      ) : null}

      <ClassAssignForm email={email} record={record} classes={[...classes].sort((a, b) => a.sortOrder - b.sortOrder)} onError={onError} />

      {currentClass ? (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-field">
            <thead>
              <tr className="text-left text-slate-500 dark:text-slate-400">
                <th className="px-3 py-2 font-medium">{t('gest:management.class.thEchelon')}</th>
                <th className="px-3 py-2 text-right font-medium">{t('gest:management.class.thRate')}</th>
                <th className="px-3 py-2 text-right font-medium">{t('gest:management.class.thDelta')}</th>
                <th className="px-3 py-2 font-medium">{t('gest:management.class.thNiveaux')}</th>
              </tr>
            </thead>
            <tbody>
              {currentClass.echelons.map((e) => {
                const isCurrent = !offGrid && e.echelon === record?.currentEchelon;
                const isReference = offGrid && ogPos?.referenceEchelon === e.echelon;
                const covering = currentClass.niveaux.filter((n) => n.echelonMax == null || e.echelon <= n.echelonMax);
                const beyondCap = cap != null && e.echelon > cap;
                return (
                  <tr key={e.echelon} className={`${isCurrent || isReference ? 'bg-brand-50 font-medium dark:bg-brand-950/40' : 'border-t border-slate-200 dark:border-slate-700'} ${beyondCap ? 'opacity-50' : ''}`}>
                    <td className="px-3 py-2 tabular-nums text-slate-700 dark:text-slate-200">
                      {e.echelon}
                      {isCurrent ? <span className="text-brand-600 dark:text-brand-400"> {t('gest:management.class.currentMark')}</span> : isReference ? <span className="text-brand-600 dark:text-brand-400"> {t('gest:management.class.refMark')}</span> : null}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-700 dark:text-slate-200">{`${formatHourly(round2(e.rate), locale)}/h`}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-500 dark:text-slate-400">{e.pctFromPrev != null ? `+${(e.pctFromPrev * 100).toFixed(1)} %` : '—'}</td>
                    <td className="px-3 py-2 text-slate-500 dark:text-slate-400">
                      {covering.length > 0 ? covering.map((n) => (currentNiveau && n.level === currentNiveau.level ? t('gest:management.class.niveauCurrent', { level: n.level }) : `N${n.level}`)).join(' · ') : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="mt-4 text-field text-slate-500 dark:text-slate-400">{t('gest:management.class.noClass')}</p>
      )}
    </Card>
  );
}
